import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { formatDataGeneral, getFileLinkIfExist } from "utils/utils";
import { transformDpatTableRows, isEvidenceLinkUploaded } from "utils/renderDpatEvidenceLink";
import { filterRecordsByDpatYearAttribute, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";

const AAP_PROGRAM = "ArLnAxhykoz";
const ACTIVITY_STATUS_ELEMENT = "SZcHb5mvjJx";
const TREE_REPORT_AVAILABLE_ELEMENT = "CRiK864sH9g";
const TREE_REPORT_FILE_ELEMENT = "hM6AUNKRbKB";

function resolveTreeReportAvailability(treePlantingPlans, reports) {
    for (const plan of treePlantingPlans) {
        const currentReport = reports.find((report) => report.trackedEntity === plan.trackedEntity);
        if (!currentReport) {
            continue;
        }

        const reportsAvailable = currentReport.dataValues.some(
            (value) => value.dataElement === TREE_REPORT_AVAILABLE_ELEMENT && value.value === "true",
        );

        if (!reportsAvailable) {
            continue;
        }

        const reportEventId = getFileLinkIfExist(reports, TREE_REPORT_FILE_ELEMENT, plan.trackedEntity);
        return {
            availability: "YES",
            report: reportEventId
                ? `https://dddp.gov.gh/api/events/files?eventUid=${reportEventId}&dataElementUid=${TREE_REPORT_FILE_ELEMENT}`
                : "Not Uploaded",
        };
    }

    return { availability: "NO", report: "NO" };
}

const ClimateChangeIntervention = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [climateChangePlan, setClimateChangePlan] = useState([]);
  const [treePlan, setTreePlan] = useState([]);
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [maxScore, setMaxScore] = useState(2);

  const { Header, Content } = Layout;
  const { Title, Text } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI5",
      area: "Environmental Health, Sanitation and Climate Action",
      maxScore,
      climateChangePlan,
      treePlan,
      scorei,
      scoreii
    }),
  }));

  const DPAT_SLUG = "sdi5.0-5.5";

  const loadFromDhis2 = useCallback(() => {
    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=${AAP_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=1000`)
      .then(result => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=${AAP_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=10000`)
            .then(resp => {
              
              const aap = isDpatScoreRelaxationExempt()
                ? filterRecordsByDpatYearAttribute(result.data.instances, year)
                : (formatDataGeneral(result.data.instances, "Year", `${year}`) || []);
              const reports = resp.data.instances;
              const climateChangePlans = formatDataGeneral(aap, "Activity Type", "Climate Adaptation") || [];
              const treePlantingPlans = formatDataGeneral(aap, "Activity Type", "Tree planting/Afforestation") || [];
              const aapTree = treePlantingPlans.length;
              const aapTotal = aap.length;
              const aapClimate = climateChangePlans.length;
              let aapClimateImplemented = 0
              let aapTreeImplemented = 0;

              climateChangePlans.forEach(plan => {
                const currentReport = reports.find(rep => rep.trackedEntity === plan.trackedEntity);

                if (currentReport) {
                  currentReport.dataValues.forEach(rep => {
                    if (rep.dataElement === "SZcHb5mvjJx" && rep.value === "Completed") {
                      aapClimateImplemented++;
                    }
                  })
                }
              });

              treePlantingPlans.forEach(plan => {
                const currentReport = reports.find(rep => rep.trackedEntity === plan.trackedEntity);

                if (currentReport) {
                  currentReport.dataValues.forEach(rep => {
                    if (rep.dataElement === ACTIVITY_STATUS_ELEMENT && rep.value === "Completed") {
                      aapTreeImplemented++;
                    }
                  })
                }
              });

              const percentage = calculatePercentage(aapClimate, aapClimateImplemented);
              const treeReport = resolveTreeReportAvailability(treePlantingPlans, reports);

              const temp = {
                key: "climate-adaptation-summary",
                aapTotal,
                aapClimate,
                aapClimateImplemented,
                percentage
              }

              const tempTree = {
                key: "tree-planting-summary",
                aapTree,
                aapTreeImplemented,
                reportAvailability: treeReport.availability === "YES" ? treeReport.report : "NO",
              }

              setClimateChangePlan([temp]);
              setTreePlan([tempTree]);

              if (percentage >= 90) {
                setScorei(1);
              } else {
                setScorei(0);
              }

              const treeReportUploaded = treeReport.availability === "YES"
                && isEvidenceLinkUploaded(treeReport.report);
              setScoreii(aapTreeImplemented > 0 && treeReportUploaded ? 1 : 0);
            })
            .catch(err => console.log(err))
        }
      })
      .catch(err => console.log(err))
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.climateChangePlan) setClimateChangePlan(snap.climateChangePlan);
      if (snap.treePlan) setTreePlan(snap.treePlan);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const calculatePercentage = (total, value) => {
    const totalNum = parseFloat(total);
    const valueNum = parseFloat(value);

    if (isNaN(totalNum) || isNaN(valueNum) || totalNum === 0) {
      return 0;
    }

    return ((valueNum / totalNum) * 100).toFixed(2);
  };


  const aapColumn = [
    {
      title: `No. of Activities in AAP ${year}`,
      dataIndex: "aapTotal",
      key: "aapTotal"
    },
    {
      title: "No. of Climate Adaptation activities in AAP",
      dataIndex: "aapClimate",
      key: "aapClimate"
    },
    {
      title: `No. of Climate Adaptation activities in AAP implemented in ${year}`,
      dataIndex: "aapClimateImplemented",
      key: "aapClimateImplemented"
    },
    {
      title: "% Implementation of Climate Adaptation activities in the AAP implemented",
      dataIndex: "percentage",
      key: "percentage"
    }
  ];

  const aapTreePlantingColumn = [
    {
      title: "No. of Tree planting activities in the AAP",
      dataIndex: "aapTree",
      key: "aapTree"
    },
    {
      title: `No. of Tree planting activities implemented in ${year}`,
      dataIndex: "aapTreeImplemented",
      key: "aapTreeImplemented"
    },
    {
      title: "Reports on Tree planting activities available (Yes/No)",
      dataIndex: "reportAvailability",
      key: "reportAvailability"
    }
  ];

  return (
    <Comment
      data={climateChangePlan}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi5.0-5.5-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.5i Actual Score:" score={scorei} />
          <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.5ii Actual Score:" score={scoreii} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "20px" }}>I- Evidence of implementation of climate adaptation activities</Title>
          <Table
            columns={aapColumn}
            dataSource={climateChangePlan}
            pagination={false} bordered />

          <Title level={4} style={{ marginTop: "20px" }}>II- Evidence of Tree planting/Afforestation activities implemented</Title>
          <Table
            columns={aapTreePlantingColumn}
            dataSource={transformDpatTableRows(treePlan, ["reportAvailability"], { reportAvailability: "View Report" })}
            pagination={false}
            bordered
            rowKey="key"
          />
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default ClimateChangeIntervention;